import { useState } from 'react';
import type { CalculatorFormData } from '../calculatorTypes';
import type { CalcTexts } from '../locales';
import { NumberInput } from '../shared/NumberInput';
import styles from '../Calculator.module.css';

interface Props {
  formData: CalculatorFormData;
  onNext: (data: Partial<CalculatorFormData>) => void;
  onBack: (data: Partial<CalculatorFormData>) => void;
  canGoBack: boolean;
  texts: CalcTexts;
}

const MAX_AGE = 24;

function ageToString(age: number | null): string {
  return age != null ? String(age) : '';
}

function stringToAge(s: string): number | null {
  const n = parseInt(s, 10);
  return isNaN(n) ? null : n;
}

export function ChildAgesStep({ formData, onNext, onBack, canGoBack, texts }: Props) {
  const childrenTotal =
    formData.children_0_5 +
    formData.children_6_13 +
    formData.children_14_17 +
    formData.children_18_24_non_working;

  const [age1, setAge1] = useState(ageToString(formData.child_1_age));
  const [age2, setAge2] = useState(ageToString(formData.child_2_age));
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!age1) errs.child_1_age = texts.validation.required;
    if (childrenTotal === 2 && !age2) errs.child_2_age = texts.validation.required;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function currentData(): Partial<CalculatorFormData> {
    return {
      child_1_age: stringToAge(age1),
      child_2_age: childrenTotal === 2 ? stringToAge(age2) : null,
    };
  }

  function handleNext() {
    if (!validate()) return;
    onNext(currentData());
  }

  const question = childrenTotal === 1
    ? texts.child_ages.question_singular
    : texts.child_ages.question_plural;

  return (
    <div className={styles.stepContent}>
      <p className={styles.stepQuestion}>{question}</p>

      <div className={styles.incomeFieldsGroup}>
        <div className={styles.incomeFieldRow}>
          <div className={styles.incomeRow}>
            <NumberInput
              id="child_1_age"
              value={age1}
              onChange={v => {
                setAge1(v);
                if (errors.child_1_age) setErrors(prev => ({ ...prev, child_1_age: '' }));
              }}
              label={childrenTotal === 2
                ? `${texts.child_ages.label_child_1} (${texts.child_ages.label_age_in_years})`
                : texts.child_ages.label_age_in_years}
              error={!!errors.child_1_age}
              max={MAX_AGE}
              prefix={null}
            />
            <label htmlFor="child_1_age" className={styles.incomeLabel}>
              {childrenTotal === 2 ? texts.child_ages.label_child_1 : texts.child_ages.label_age_in_years}
            </label>
          </div>
          {errors.child_1_age && <p className={styles.validationError}>{errors.child_1_age}</p>}
        </div>

        {childrenTotal === 2 && (
          <div className={styles.incomeFieldRow}>
            <div className={styles.incomeRow}>
              <NumberInput
                id="child_2_age"
                value={age2}
                onChange={v => {
                  setAge2(v);
                  if (errors.child_2_age) setErrors(prev => ({ ...prev, child_2_age: '' }));
                }}
                label={`${texts.child_ages.label_child_2} (${texts.child_ages.label_age_in_years})`}
                error={!!errors.child_2_age}
                max={MAX_AGE}
                prefix={null}
              />
              <label htmlFor="child_2_age" className={styles.incomeLabel}>
                {texts.child_ages.label_child_2}
              </label>
            </div>
            {errors.child_2_age && <p className={styles.validationError}>{errors.child_2_age}</p>}
          </div>
        )}
      </div>

      <div className={styles.stepNav}>
        <button
          type="button"
          className={styles.btnBack}
          onClick={() => onBack(currentData())}
          disabled={!canGoBack}
        >
          {'< '}{texts.common.buttons.back}
        </button>
        <button type="button" className={styles.btnSecondary} onClick={handleNext}>
          {texts.common.buttons.continue}
        </button>
      </div>
    </div>
  );
}
